import {useState} from 'react'
import {useIntl} from 'react-intl'
import {useQuery, useQueryClient} from 'react-query'
import {useFormik} from 'formik'
import clsx from 'clsx'
import {Toast} from '../../../../../../_metronic/partials/widgets'
import {ID, QUERIES} from '../../../../../../_metronic/helpers'
import {Loading} from '../../../../../../_metronic/partials/components/loading/Loading'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import Select from 'react-select'
import {getTeams} from '../../../../../../_metronic/partials/modals/team/core/_requests'
import {editProjectTeams} from './core/_requests'
import {Team} from '../../../../../../_metronic/partials/modals/team/core/_models'

type Props = {
  project_id: ID
  handleClose: () => void
  teams?: Team[]
}

const EditProjectTeamsModalForm = ({handleClose, project_id, teams}: Props) => {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const [selectedTeams, setSelectedTeams] = useState(teams)

  const {data: dataTeam} = useQuery(
    `${QUERIES.TEAMS_LIST}-all`,
    async () => (await getTeams()).data,
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  )

  const formik = useFormik({
    initialValues: {team_ids: teams?.map((item) => item.id)},
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      setSubmitting(true)
      try {
        await editProjectTeams(project_id!, values)
        Toast.fire({
          icon: 'success',
          title: intl.formatMessage(
            {id: 'ALERT.LABEL.SUCCESS_UPDATE_ITEM'},
            {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.TEAMS'})}
          ),
          customClass: {
            popup: 'bg-light-success',
            title: 'text-success',
          },
        })
        queryClient.invalidateQueries()
        resetForm()
        handleClose()
      } catch (error: any) {
        Toast.fire({
          icon: 'error',
          title: error.response.data.message,
          customClass: {
            popup: 'bg-light-danger',
            title: 'text-danger',
          },
        })
      } finally {
        setSubmitting(true)
      }
    },
  })

  async function onSubmit(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault()
    formik.submitForm()
  }

  return (
    <>
      <form
        id='kt_modal_edit_project_teams_form'
        className='form'
        onSubmit={formik.handleSubmit}
        noValidate
      >
        <>
          <div className='row mb-5'>
            <p className='mb-8'>
              Vincule equipes a este projeto para controlar o acesso. Ao adicionar pelo menos uma
              equipe, o projeto se tornará privado, e somente os membros das equipes vinculadas
              poderão visualizá-lo. Se nenhuma equipe estiver associada, o projeto permanecerá
              público e acessível a todos os usuários.
            </p>

            <div className='col-12'>
              <label htmlFor='team' className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                <span className=''>{intl.formatMessage({id: 'FORM.INPUT.NAME.TEAMS'})}</span>
                <OverlayTrigger
                  placement='top'
                  delay={{show: 50, hide: 100}}
                  overlay={
                    <Tooltip>
                      Apenas os usuários da equipe associada ao projeto poderão visualizar suas
                      tarefas
                    </Tooltip>
                  }
                >
                  <i className='fas fa-exclamation-circle ms-2 fs-7'></i>
                </OverlayTrigger>
              </label>
              <Select
                menuPosition='fixed'
                closeMenuOnSelect={false}
                isSearchable={false}
                options={dataTeam}
                isMulti={true}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.name!)}
                getOptionValue={(option) =>
                  typeof option === 'string' ? option : String(option.id)
                }
                noOptionsMessage={() => 'Nenhuma equipe disponível'}
                onChange={(values) => {
                  setSelectedTeams(values.map((item) => item))
                  formik.setFieldValue(
                    'team_ids',
                    values.map((item) => item.id)
                  )
                }}
                value={selectedTeams}
                placeholder={'Selecione as equipes'}
                classNames={{
                  control: (state) => clsx('border form-control form-control-solid p-1'),
                  input: (state) => 'text-dark form-control form-control-solid',
                  singleValue: (state) => 'text-dark form-control form-control-solid',
                  menu: (state) => 'bg-body',
                }}
                styles={{
                  option: (styles, {data, isDisabled, isFocused, isSelected}) => {
                    return {
                      ...styles,
                      backgroundColor: isDisabled
                        ? undefined
                        : isSelected
                        ? '#369bff70'
                        : isFocused
                        ? '#369bff70'
                        : undefined,
                      color: isDisabled ? '#ccc' : isSelected ? '#181C32' : undefined,
                    }
                  },
                  multiValue: (provided: any) => ({
                    ...provided,
                    borderRadius: '12px',
                    backgroundColor: '#007bff',
                    padding: '0px 4px',
                  }),
                  multiValueLabel: (provided: any) => ({
                    ...provided,
                    color: 'white',
                  }),
                  multiValueRemove: (provided: any) => ({
                    ...provided,
                    padding: '0px',
                    margin: '0px',
                    color: 'white',
                    cursor: 'pointer',
                    ':hover': {
                      backgroundColor: 'none',
                    },
                  }),
                  menuList: (provided) => ({
                    ...provided,
                    maxHeight: 160,
                    overflowY: 'auto',
                  }),
                }}
              />
            </div>
          </div>

          <div className='text-center pt-7'>
            <button type='button' className='btn btn-primary btn-sm px-8' onClick={onSubmit}>
              <span className='indicator-label'>
                {intl.formatMessage({id: 'PROJECT.LABEL.UPDATE_VISIBILITY'})}
              </span>
            </button>
          </div>
        </>
      </form>
      {formik.isSubmitting && <Loading />}
    </>
  )
}

export {EditProjectTeamsModalForm}
